import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableHead, TableCell } from '@material-ui/core';

import { SharesItemFragment } from 'api';
import { ContentProvider } from 'providers';
import { EmptyState, Loaders, ResponsiveTable } from 'components';
import { useNotify } from 'hooks';
import { ShareButton } from 'features/content/widgets';
import { useSharesItems } from 'features/activity/hooks';

import DateCell from './Cells/Date';
import ImageCell from './Cells/Image';
import PastShareCell from './Cells/PastShare';
import StatCell from './Cells/Stat';
import { RowHead, RowBody } from './TableRow';
import ListLoader from '../ListLoader';

const PastShares: FC = () => {
  const notify = useNotify();
  const { t } = useTranslation();

  const { results, isExhausted, isEmpty, isInitialLoad, sentinelRef } = useSharesItems(
    {
      scheduled: false,
      data: {
        limit: 10,
      },
    },
    {
      onError: notify.queryError,
    }
  );

  if (isInitialLoad) {
    return <ListLoader />;
  }

  if (isEmpty) {
    return <EmptyState name="past" />;
  }

  function hasContent(sharesItem: SharesItemFragment) {
    return !!sharesItem.content;
  }

  return (
    <Fragment>
      <ResponsiveTable>
        <Table>
          <TableHead>
            <RowHead>
              <TableCell size="small" colSpan={3}>
                {t('sharing:sharedAt')}
              </TableCell>
              <TableCell size="small">{t('common:click_plural')}</TableCell>
              <TableCell size="small">{t('common:engagement_plural')}</TableCell>
              <TableCell />
            </RowHead>
          </TableHead>
          <TableBody>
            {results.map(shareItem => (
              <RowBody key={shareItem.id}>
                <DateCell time={shareItem.shared_at} />
                <ImageCell content={shareItem.content} />
                <PastShareCell item={shareItem} />
                <StatCell value={shareItem.clicks || 0} />
                <StatCell value={shareItem.engagements || 0} />
                <TableCell>
                  {hasContent(shareItem) && (
                    <ContentProvider value={{ content: shareItem.content }}>
                      <ShareButton size="small" />
                    </ContentProvider>
                  )}
                </TableCell>
              </RowBody>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
      {!isExhausted && (
        <div ref={sentinelRef}>
          <Loaders.Standard />
        </div>
      )}
    </Fragment>
  );
};

export default PastShares;
